// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { AutodeskUrn, DeepReadonly } from '@evolvemep/api-ts';
import { Button, Flex, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { ForgeViewerSelection } from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

type Props = {
  selection: DeepReadonly<ForgeViewerSelection[]>;
  onEndSelection: () => void;
  urn: AutodeskUrn;
};

const StartRequestFields = ({ selection, onEndSelection, urn }: Props) => {
  const { addRequest } = useSpoolRequestsViewerState();
  const form = useForm<{ name: string }>({
    initialValues: {
      name: '',
    },
    validate: {
      name: (v) => !v,
    },
  });

  const onSubmit = async ({ name }: typeof form.values) => {
    await addRequest({
      autodeskCloudModelId: urn,
      externalIds: selection.map(({ externalId }) => externalId),
      name,
    });
    notifications.show({
      title: 'Request created',
      message: 'Your request for a new spool has successfully been created.',
      color: 'green',
    });
    onEndSelection();
  };

  useFormPopover(form, onSubmit);

  return <TextInput autoFocus data-autofocus label="Spool Name" {...form.getInputProps('name')} />;
};

export const StartRequestPopover = ({ selection, onEndSelection, urn }: Props) => {
  const [opened, setOpened] = useState(false);
  const { isMutationRunning } = useSpoolRequestsViewerState();

  return (
    <Flex gap="xs">
      <Button
        disabled={isMutationRunning}
        variant="light"
        leftSection={<EvolveIcon icon="Close" />}
        onClick={onEndSelection}
      >
        Cancel
      </Button>
      <FormPopover
        title="New spool request"
        opened={opened}
        onClose={() => setOpened(false)}
        confirmationText="Create"
        target={
          <Button
            disabled={selection.length === 0}
            loading={isMutationRunning}
            leftSection={<EvolveIcon icon="Check" />}
            onClick={() => {
              setOpened(true);
            }}
          >
            Create request
          </Button>
        }
      >
        <StartRequestFields selection={selection} onEndSelection={onEndSelection} urn={urn} />
      </FormPopover>
    </Flex>
  );
};
